<!-- /Users/xingyunx/workplace/ws/RingcentralAnalysisPortal/src/components/settings/PasswordChangeForm.vue -->
<!-- Reusable password change form component -->
<!-- Used in both onboarding flow and settings page -->
<!-- RELEVANT FILES: src/components/dashboard/PasswordSetupStep.vue, src/pages/settings/change-password.vue, src/services/aws-auth.service.ts -->
<script setup lang="ts">
import { updatePassword } from 'aws-amplify/auth';
import { AlertCircle, Check, Eye, EyeOff } from 'lucide-vue-next';
import { computed, ref } from 'vue';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface Props {
  requireCurrentPassword?: boolean;
  showCancelButton?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  requireCurrentPassword: true,
  showCancelButton: false,
});

const emit = defineEmits<{
  success: [];
  cancel: [];
}>();

const currentPassword = ref('');
const newPassword = ref('');
const confirmPassword = ref('');
const showCurrentPassword = ref(false);
const showNewPassword = ref(false);
const showConfirmPassword = ref(false);
const isSubmitting = ref(false);
const errorMessage = ref('');
const successMessage = ref('');

// Password validation rules
const hasMinLength = computed(() => newPassword.value.length >= 8);
const hasUppercase = computed(() => /[A-Z]/.test(newPassword.value));
const hasNumber = computed(() => /\d/.test(newPassword.value));
const hasSpecialChar = computed(() => /[!@#$%^&*(),.?":{}|<>]/.test(newPassword.value));
const passwordsMatch = computed(() => {
  if (!confirmPassword.value) {return null;}
  return newPassword.value === confirmPassword.value;
});

const isPasswordValid = computed(() => {
  return hasMinLength.value && hasUppercase.value && hasNumber.value && hasSpecialChar.value;
});

const canSubmit = computed(() => {
  const passwordValid = isPasswordValid.value && passwordsMatch.value === true;
  if (props.requireCurrentPassword) {
    return passwordValid && currentPassword.value.length > 0 && !isSubmitting.value;
  }
  return passwordValid && !isSubmitting.value;
});

async function handleChangePassword() {
  if (!canSubmit.value) {return;}

  try {
    isSubmitting.value = true;
    errorMessage.value = '';
    successMessage.value = '';

    // Call AWS Amplify to update password
    await updatePassword({
      oldPassword: currentPassword.value,
      newPassword: newPassword.value,
    });

    successMessage.value = 'Password changed successfully!';
    console.log('✅ Password changed successfully');

    // Reset form
    currentPassword.value = '';
    newPassword.value = '';
    confirmPassword.value = '';

    // Emit success event
    emit('success');
  } catch (error: any) {
    errorMessage.value = error.message || 'Failed to change password. Please try again.';
    console.error('Password change failed:', error);
  } finally {
    isSubmitting.value = false;
  }
}

function handleCancel() {
  emit('cancel');
}
</script>

<template>
  <form class="space-y-4" @submit.prevent="handleChangePassword">
    <!-- Current Password (optional) -->
    <div v-if="requireCurrentPassword" class="space-y-2">
      <Label for="current-password">Current Password</Label>
      <div class="relative">
        <Input
          id="current-password"
          v-model="currentPassword"
          :type="showCurrentPassword ? 'text' : 'password'"
          placeholder="Enter your current password"
          class="pr-10"
          :disabled="isSubmitting"
        />
        <button
          type="button"
          class="absolute right-2 top-1/2 -translate-y-1/2 p-2 hover:bg-accent rounded-md transition-colors"
          @click="showCurrentPassword = !showCurrentPassword"
        >
          <Eye v-if="!showCurrentPassword" class="h-4 w-4 text-muted-foreground" />
          <EyeOff v-else class="h-4 w-4 text-muted-foreground" />
        </button>
      </div>
    </div>

    <!-- New Password -->
    <div class="space-y-2">
      <Label for="new-password">New Password</Label>
      <div class="relative">
        <Input
          id="new-password"
          v-model="newPassword"
          :type="showNewPassword ? 'text' : 'password'"
          placeholder="Enter your new password"
          class="pr-10"
          :disabled="isSubmitting"
        />
        <button
          type="button"
          class="absolute right-2 top-1/2 -translate-y-1/2 p-2 hover:bg-accent rounded-md transition-colors"
          @click="showNewPassword = !showNewPassword"
        >
          <Eye v-if="!showNewPassword" class="h-4 w-4 text-muted-foreground" />
          <EyeOff v-else class="h-4 w-4 text-muted-foreground" />
        </button>
      </div>
    </div>

    <!-- Confirm Password -->
    <div class="space-y-2">
      <Label for="confirm-password">Confirm New Password</Label>
      <div class="relative">
        <Input
          id="confirm-password"
          v-model="confirmPassword"
          :type="showConfirmPassword ? 'text' : 'password'"
          placeholder="Confirm your new password"
          class="pr-10"
          :disabled="isSubmitting"
        />
        <button
          type="button"
          class="absolute right-2 top-1/2 -translate-y-1/2 p-2 hover:bg-accent rounded-md transition-colors"
          @click="showConfirmPassword = !showConfirmPassword"
        >
          <Eye v-if="!showConfirmPassword" class="h-4 w-4 text-muted-foreground" />
          <EyeOff v-else class="h-4 w-4 text-muted-foreground" />
        </button>
      </div>
      <p v-if="passwordsMatch === false" class="text-sm text-destructive flex items-center gap-1.5">
        <X class="h-3.5 w-3.5" />
        Passwords do not match
      </p>
      <p
        v-else-if="passwordsMatch === true"
        class="text-sm text-green-600 flex items-center gap-1.5"
      >
        <Check class="h-3.5 w-3.5" />
        Passwords match
      </p>
    </div>

    <!-- Password requirements -->
    <div class="space-y-2">
      <p class="text-sm font-medium">Your new password must contain:</p>
      <ul class="space-y-1.5 text-sm">
        <li
          :class="hasMinLength ? 'text-green-600' : 'text-muted-foreground'"
          class="flex items-center gap-2"
        >
          <Check v-if="hasMinLength" class="h-3.5 w-3.5" />
          <span v-else class="h-3.5 w-3.5 rounded-full border-2 border-muted-foreground" />
          At least 8 characters
        </li>
        <li
          :class="hasUppercase ? 'text-green-600' : 'text-muted-foreground'"
          class="flex items-center gap-2"
        >
          <Check v-if="hasUppercase" class="h-3.5 w-3.5" />
          <span v-else class="h-3.5 w-3.5 rounded-full border-2 border-muted-foreground" />
          An uppercase letter (A-Z)
        </li>
        <li
          :class="hasNumber ? 'text-green-600' : 'text-muted-foreground'"
          class="flex items-center gap-2"
        >
          <Check v-if="hasNumber" class="h-3.5 w-3.5" />
          <span v-else class="h-3.5 w-3.5 rounded-full border-2 border-muted-foreground" />
          A number (0-9)
        </li>
        <li
          :class="hasSpecialChar ? 'text-green-600' : 'text-muted-foreground'"
          class="flex items-center gap-2"
        >
          <Check v-if="hasSpecialChar" class="h-3.5 w-3.5" />
          <span v-else class="h-3.5 w-3.5 rounded-full border-2 border-muted-foreground" />
          A special character (e.g., !, @, #, $)
        </li>
      </ul>
    </div>

    <!-- Success message -->
    <Alert
      v-if="successMessage"
      class="border-green-600 bg-green-50 text-green-900 dark:bg-green-900/20 dark:text-green-400"
    >
      <Check class="h-4 w-4 text-green-600" />
      <AlertDescription>{{ successMessage }}</AlertDescription>
    </Alert>

    <!-- Error message -->
    <Alert v-if="errorMessage" variant="destructive">
      <AlertCircle class="h-4 w-4" />
      <AlertDescription>{{ errorMessage }}</AlertDescription>
    </Alert>

    <!-- Action buttons -->
    <div class="flex flex-col sm:flex-row gap-3 pt-2">
      <Button
        type="submit"
        class="flex-1 w-full sm:w-auto"
        :loading="isSubmitting"
        :disabled="!canSubmit"
      >
        <span v-if="isSubmitting">Changing Password...</span>
        <span v-else>Change Password</span>
      </Button>

      <Button
        v-if="showCancelButton"
        type="button"
        variant="outline"
        class="w-full sm:w-auto"
        @click="handleCancel"
      >
        Cancel
      </Button>
    </div>
  </form>
</template>
